<template>
  <div class="h-screen flex items-center offwhite navbar-adjusted">
    <div class="w400 mxauto">
      <div class="white shadow br8 relative">
        <!-- Header -->
        <div class="px40 pt40 pb24 center">
          <div class="promo-icon mb16 mxauto">
            🎓
          </div>
          <h3 class="black bold mb8">Acegate Promotion</h3>
          <p class="grey fs14">Redeem your coupon for free quizzes</p>
        </div>

        <div class="px40 pb40">
          <!-- University Selection -->
          <div class="mb24">
            <label style="color: black" class="mb8 block uppercase">Institution</label>
            <div class="relative pr30">
              <select
                v-model="selectedUniversity"
                class="wfill p12 br4 ui input"
                :class="{ 'error-border': universityError, lineHeight: '20px' }"
              >
                <option value="" disabled>Choose your university...</option>
                <option 
                  v-for="university in universities" 
                  :key="university.id" 
                  :value="university.id"
                >
                  {{ university.name }}
                </option>
              </select>
              <div style="color: black" class="absolute dropdown-arrow">
                ▼
              </div>
            </div>
            <p v-if="universityError" class="fs12 red mt8">
              Please select your university
            </p>
          </div>

          <!-- Coupon Code Input -->
          <div class="mb32">
            <label style="color: black" class="mb8 block black uppercase">Coupon Code</label>
            <input
              v-model="couponCode"
              type="text"
              placeholder="Enter your coupon code"
              class="wfill p12 br4 ui input uppercase"
              :class="{ 'error-border': couponError }"
              style="font-family: monospace; letter-spacing: 2px"
              @input="couponCode = $event.target.value.toUpperCase()"
            />
            <p v-if="couponError" class="fs12 red mt8">
              {{ couponError }}
            </p>
          </div>

          <!-- Redeem Button -->
          <button
            @click="redeemCoupon"
            class="wfill brown500 py16 br4 bold relative"
          >
            <div v-if="isLoading" class="flex centered">
              <div class="spinner mr12"></div>
              Redeeming...
            </div>
            <span v-else>Redeem Coupon</span>
          </button>

          <!-- Success Message -->
          <div v-if="successMessage" class="green-bg mt24 p16 br4 flex">
            <div class="green mr12 bold" style="font-size: 20px">✓</div>
            <p class="fs12 green">{{ successMessage }}</p>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: "AcegatePromo",
  data() {
    return {
      selectedUniversity: "",
      couponCode: "",
      universities: [],
      isLoading: false,
      universityError: false,
      couponError: "",
      successMessage: ""
    };
  },
  mounted() {
    this.loadUniversities();
  },
  methods: {
    async loadUniversities() {
      try {
        const response = await this.$req.get("universities-with-coupons");
        this.universities = response.data;
      } catch (error) {
        console.error("Failed to load universities:", error);
        this.$bus.notify("Failed to load universities");
      }
    },
    async redeemCoupon() {
      this.resetErrors();
      
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;
      
      try {
        const response = await this.$req.post(
          "redeem-acegate-coupon",
          {
            university_id: this.selectedUniversity,
            coupon_code: this.couponCode,
            user_id: this.$bus.user.id
          },
          this.$bus.headers
        );

        if (response.data.success) {
          this.successMessage = `Congratulations! You've received ${response.data.quizzes_added || 'free'} quiz${response.data.quizzes_added > 1 ? 's' : ''} for Acegate!`;
          this.couponCode = "";
          this.selectedUniversity = "";
          
          setTimeout(() => {
            this.$router.push("/acegate");
          }, 2000);
        } else {
          this.couponError = response.data.message || "Invalid coupon code";
        }
      } catch (error) {
        console.error("Coupon redemption failed:", error);

        if (error.response && error.response.status === 401) {
          this.couponError = "Access token expired. Please logout and login again.";
        } else if (error.response && error.response.data && error.response.data.message) {
          this.couponError = error.response.data.message;
        } else {
          this.couponError = "Failed to redeem coupon. Please try again.";
        }
      } finally {
        this.isLoading = false;
      }
    },
    validateForm() {
      let isValid = true;

      if (!this.selectedUniversity) {
        this.universityError = true;
        isValid = false;
      }

      if (!this.couponCode.trim()) {
        this.couponError = "Please enter a coupon code";
        isValid = false;
      } else if (this.couponCode.length < 3) {
        this.couponError = "Coupon code must be at least 3 characters";
        isValid = false;
      }

      return isValid;
    },
    resetErrors() {
      this.universityError = false;
      this.couponError = "";
      this.successMessage = "";
    }
  },
  watch: {
    selectedUniversity() {
      if (this.universityError && this.selectedUniversity) {
        this.universityError = false;
      }
    },
    couponCode() {
      if (this.couponError && this.couponCode.trim()) {
        this.couponError = "";
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../styles.scss";

.error-border {
  border-color: map-get($colors, red) !important;
}

.error-border:focus {
  border-color: map-get($colors, red) !important;
  box-shadow: 0 0 0 2px rgba(224, 112, 116, 0.2) !important;
}

.disabled-btn {
  opacity: 0.5;
  cursor: not-allowed;
  
  &:hover {
    background: map-get($colors, brown500) !important;
  }
}

.green-bg {
  background: rgba(144, 155, 89, 0.1);
  border: 1px solid rgba(144, 155, 89, 0.3);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.ui.input {
  border: 1px solid #ccc !important;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    border-color: map-get($colors, brown500) !important;
    box-shadow: 0 0 0 2px rgba(185, 135, 68, 0.2) !important;
    outline: none !important;
  }
}

select.ui.input {
  appearance: none;
  background-image: none;
  cursor: pointer;
}

button {
  transition: all 0.2s ease;
  
  &:hover:not(.disabled-btn) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:active:not(.disabled-btn) {
    transform: translateY(0);
  }
}

.shadow {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.block {
  display: block;
}

.items-center {
  align-items: center;
}

.dropdown-arrow {
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 12px;
}

.promo-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
}

.navbar-adjusted {
  padding-bottom: 73px; /* Account for fixed navbar height */
}
</style>